import { useEffect, useState } from 'react';
import { UseFieldArrayAppend, UseFieldArrayReplace, UseFieldArrayUpdate, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button, Input, Modal, Select, SpinnerOverlay, SubmissionButtons } from '@components/common';
import SwapUserReferenceForm from '@components/ModalComponents/SwapUserReferenceForm';

import { useQueryAllOrganisations } from '@hooks/organisation/useQueryAllOrganisations';
import { useQueryOrganisationProfileById } from '@hooks/organisation/useQueryOrganisationProfile';
import { useQueryRoles } from '@hooks/roles/useQueryRoles';

import { isEmpty } from '@utils/common';
import { mapDropdownOptions } from '@utils/formatter';
import { generateRolesForCreateUser, getRoleSelectionHelper } from '@utils/rolesUtils';
import { useModal } from '@components/common/Modal/utils';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IOrganisationEntity } from '@interfaces/organisation/organisations';
import { IUserEntity } from '@interfaces/user/user';
import { IOrganisationField } from '../interfaces';

import { EUserRoles } from '@enums/common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { adminRoles } from '../constants';
import { EAssignOrganisationFields } from './constants';

import { getOrganizationFormSchema } from './schema';

import styles from './AssignOrganisationForm.module.scss';

interface IAssignOrganisationProps {
	editableUser?: IUserEntity;
	assignedOrganisation?: IOrganisationField;
	isExistUserAdmin: boolean;
	isEditMode: boolean;
	isSingleOrganisationMode: boolean;
	usedOrganisationIds: string[];
	isViewMode?: boolean;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	append: UseFieldArrayAppend<any, string>;
	replace: UseFieldArrayReplace<any, string>;
	update: UseFieldArrayUpdate<any, string>;
	closeModals: () => void;
	onSubmit: () => void;
}

interface IAssignOrganisationForm {
	organisation: TSelectOption<string>;
	roles: TSelectOption[] | null;
	jobTitle: string;
}

const AssignOrganisationForm = ({
	assignedOrganisation,
	editableUser,
	usedOrganisationIds,
	isEditMode,
	isViewMode,
	isSingleOrganisationMode,
	setIsEditMode,
	setIsDirtyFormModal,
	closeModals,
	onSubmit,
	append,
	replace,
	update,
}: IAssignOrganisationProps) => {
	const [allRoles, setAllRoles] = useState<TSelectOption[]>([]);
	const [isDirtySwapUserForm, setIsDirtySwapUserForm] = useState(false);
	const [isSwapUserModal, setIsSwapUserModal] = useState(false);
	const [isRequiredJobTitle, setIsRequiredJobTitle] = useState(false);

	const adminRolesList = adminRoles.map(({ role }) => role);
	const isAdmin = editableUser?.roles?.some((item) => adminRolesList.includes(item.name));

	const isFieldsDisabled = isViewMode && !isEditMode;

	const { onClose } = useModal();

	const schema = getOrganizationFormSchema(isRequiredJobTitle);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		clearErrors,
		control,
	} = useForm<IAssignOrganisationForm>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			[EAssignOrganisationFields.jobTitle]: assignedOrganisation?.jobTitle || '',
			[EAssignOrganisationFields.organisation]: assignedOrganisation?.organisation || null,
			[EAssignOrganisationFields.roles]: null,
		},
	});

	const formatOrganisation = (organisationsAll: IOrganisationEntity[]) => mapDropdownOptions({
		items: organisationsAll,
		valueKey: 'id',
		labelKey: 'name',
	}).filter(item => !usedOrganisationIds.includes(item.value));

	const { organisationsAll, formattedOrganisations } = useQueryAllOrganisations(formatOrganisation);

	const roles = watch(EAssignOrganisationFields.roles);
	const organisation = watch(EAssignOrganisationFields.organisation);
	const adminRoleNames = adminRoles.map(({ role }) => role);
	const jobTitle = watch(EAssignOrganisationFields.jobTitle);

	const isEditRolesMode = !!assignedOrganisation;
	const isSubmitDisabled = !organisation || !roles?.length || isRequiredJobTitle && !jobTitle;

	const { profile } = useQueryOrganisationProfileById(organisation?.value);

	const currentOrganisationTypes = organisationsAll
		.find(item => item.id === organisation?.value)?.types
		.map(item => item.name) || [];

	const isLastAdmin = isSingleOrganisationMode
		? editableUser?.isLastAdmin
		: editableUser?.isLastAdminInAnyOrganization;

	const isExistUserAdmin = editableUser ? (!isAdmin || !isLastAdmin) : true;

	const {
		roles: rolesToCreate,
		isLoadingRoles,
	} = useQueryRoles({
		organizationId:
			organisation?.value
			|| assignedOrganisation?.organisation?.value
			|| editableUser?.organization?.id,
		isExistUserAdmin,
	});

	const toggleSwapUserReferenceModal = () => setIsSwapUserModal(!isSwapUserModal);
	const closeSwapUserModal = () => toggleSwapUserReferenceModal();

	const addNewOrganisationHandler = (values: IAssignOrganisationForm) => {
		const isAdminAdded = values?.roles
			?.some((item) => item?.value && adminRoleNames.includes(item.value.toString()));

		const dto = {
			organizationId: values.organisation?.value,
			title: values.organisation?.label,
			avatar: profile.avatar?.id || '',
			organisation: values.organisation,
			roles: values.roles || [],
			isActive: profile.isActive,
			jobTitle: values.jobTitle,
			adminCount: isAdminAdded ? profile.adminCount + 1 : profile.adminCount,
			index: assignedOrganisation?.index !== undefined
				? assignedOrganisation.index
				: usedOrganisationIds.length,
		};
		if (isEditMode && assignedOrganisation?.index !== undefined) {
			update(assignedOrganisation.index, dto);
		} else {
			if (isSingleOrganisationMode) {
				replace([dto]);
			} else {
				append(dto);
			}
		}
	};

	const negativeHandler = () => {
		if (isFieldsDisabled) {
			setIsEditMode(true);

			return;
		}

		onClose();
	};

	const positiveHandler = () => {
		if (isFieldsDisabled) {
			onClose();

			return;
		}

		handleSubmit(addNewOrganisationHandler)().then(() => {
			if (isSingleOrganisationMode) {
				onSubmit();
			} else {
				closeModals();
			}
		});
	};

	const onSelectChange = (options: Array<TSelectOption<string>>) => {
		if (isEmpty(options)) {
			setValue(EAssignOrganisationFields.roles, null, { shouldValidate: true });
		} else {
			const {
				isSomeAdminRolesRequired,
				getRolesWithRelevantAdmins,
				getRolesWithoutRelevantAdmins,
				// eslint-disable-next-line react-hooks/rules-of-hooks
			} = getRoleSelectionHelper({
				multiSelectOptions: options,
				allRoles,
				currentOrganisationTypes,
			});

			if (isSomeAdminRolesRequired) {
				const isPartialRelevantAdminsSelected = roles?.some((role: TSelectOption) =>
					role?.value === EUserRoles.assetOwnerOrganizationAdministrator
					|| role?.value === EUserRoles.networkOperatorOrganizationAdministrator
					|| role?.value === EUserRoles.assetConsumerOrganizationAdministrator) || false;

				if (isPartialRelevantAdminsSelected) {
					setValue(EAssignOrganisationFields.roles, getRolesWithoutRelevantAdmins());
				} else {
					setValue(EAssignOrganisationFields.roles, getRolesWithRelevantAdmins());
				}
			} else {
				setValue(EAssignOrganisationFields.roles, options);
			}

			clearErrors(EAssignOrganisationFields.roles);
		}
	};

	useEffect(() => {
		if (isFieldsDisabled) {
			setValue(
				EAssignOrganisationFields.roles,
				assignedOrganisation?.roles || null,
			);

			return;
		}

		if (allRoles?.length && organisation) {
			setValue(
				EAssignOrganisationFields.roles,
				isEditMode && !!assignedOrganisation
					? allRoles.filter((roleItem: TSelectOption) => {
						return assignedOrganisation?.roles?.some((orgRole) =>
							roleItem?.name === orgRole?.value);
					})
					: isExistUserAdmin
						? allRoles.filter((roleItem: TSelectOption) => roleItem?.isFixed)
						: null,
			);
		} else if (!organisation) {
			setValue(EAssignOrganisationFields.roles, null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, isFieldsDisabled, allRoles, organisation]);

	useEffect(() => {
		if (!rolesToCreate.length) {
			return;
		}

		const generatedRoles = generateRolesForCreateUser({
			editableUser,
			rolesToCreate,
			currentOrganisationTypes,
			isExistUserAdmin,
		});

		setAllRoles(generatedRoles.map(item => ({ ...item })));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rolesToCreate, isExistUserAdmin, editableUser]);

	useEffect(() => {
		const isExistRole = !!(roles
			?.some((roleItem: TSelectOption) => roleItem?.name === EUserRoles.assetOwnerContact));

		clearErrors(EAssignOrganisationFields.jobTitle);
		setIsRequiredJobTitle(isExistRole);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [roles, jobTitle]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	return (
		<div className={styles.container}>
			{isLoadingRoles && <SpinnerOverlay />}
			{editableUser && (
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtySwapUserForm}
					isOpen={isSwapUserModal}
					minWidth={MODAL_WIDTH_580}
					onClose={toggleSwapUserReferenceModal}
					title='Swap user reference'
					width='auto'
				>
					<SwapUserReferenceForm
						closeForm={closeSwapUserModal}
						currentUser={editableUser}
						organisationId={assignedOrganisation?.organizationId}
						setIsDirtyForm={setIsDirtySwapUserForm}
					/>
				</Modal>
			)}
			<div className={styles.organisation}>
				<Select
					control={control}
					disabled={isEditRolesMode || isFieldsDisabled}
					errorMessage={errors.organisation?.message}
					isError={!!errors?.organisation}
					label='Organisation'
					maxWidth='230px'
					minWidth='230px'
					name={EAssignOrganisationFields.organisation}
					options={formattedOrganisations}
					placeholder='Select organisation'
					required
				/>
				<Input
					containerClassName={styles.jobTitle}
					disabled={isFieldsDisabled}
					errorMessage={errors?.jobTitle?.message}
					label='Job Title'
					name={EAssignOrganisationFields.jobTitle}
					placeholder='Enter job title'
					register={register}
					required={isRequiredJobTitle}
				/>
			</div>
			<div className={styles.role}>
				<Select
					closeMenuOnSelect={false}
					control={control}
					disabled={!organisation || isFieldsDisabled}
					errorMessage={errors.roles?.message}
					handleChange={onSelectChange}
					isError={!!errors?.roles}
					isMulti
					label='Role'
					name={EAssignOrganisationFields.roles}
					options={allRoles}
					placeholder='Select roles'
					required
					value={roles}
					withFixedValue
				/>
			</div>
			<div className={styles.footer}>
				<div>
					{isSingleOrganisationMode && editableUser && (
						<Button
							className={styles.swapUserButton}
							disabled={isFieldsDisabled}
							onClick={toggleSwapUserReferenceModal}
							text='Swap user reference'
							withoutBackground
						/>
					)}
				</div>
				<SubmissionButtons
					className={styles.submitButtons}
					disabledPositive={isSubmitDisabled}
					negativeHandler={negativeHandler}
					negativeLabel={isFieldsDisabled ? 'Edit user' : 'Cancel'}
					positiveHandler={positiveHandler}
					positiveLabel={isFieldsDisabled ? 'Close' : isEditRolesMode ? 'Save' : 'Proceed'}
				/>
			</div>
		</div>
	);
};

export default AssignOrganisationForm;
